import { ElementType, KeyboardEvent, useCallback } from 'react';
import { css } from '@emotion/react';
import { Template } from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import { TemplatorV2Queries } from '@frontend/api-templator-v2';
import { getUser } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useAppScopeStore } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import {
  DropdownField,
  NakedButton,
  PolymorphicComponentPropWithoutRef,
  SearchField,
  Text,
  useDebouncedValue,
  useFormField,
} from '@frontend/design-system';
import { SETTINGS_MODAL_INTERACTION_DELAY, TemplateTypeTitleMap } from '../../../constants';
import { TrackingPrefixes } from '../../../tracking-prefixes';
import { STATIC_TEMPLATE_ID, TemplateTypesWithProgressiveFlows } from '../../constants';
import { useTemplateTypeMap, useUpdateTemplateLastTimestamp } from '../../hooks';
import { ManualTemplateTypes, UseTemplateFlowResult } from '../../types';
import { getStaticTemplate, sortTemplatesByLastUsage } from '../../utils';
import { getManualTemplateTrackingId } from '../../utils/get-template-tracking-id';
import { TemplateListItem } from './template-list-item';

const templateMatchesSearch = (
  { name, templateString }: { name: string; templateString: string },
  searchValue: string
) => {
  if (!searchValue) return true;
  const lowerCaseSearchValue = searchValue.toLowerCase();
  return (
    name.toLowerCase().includes(lowerCaseSearchValue) || templateString.toLowerCase().includes(lowerCaseSearchValue)
  );
};

export type MultiTabTemplateSelectorProps = {
  groupIds: string[];
  templateTypes: ManualTemplateTypes[];
  staticDefaultTemplateTypes?: ManualTemplateTypes[];
  onSelectTemplate: (template: Template) => void;
  hideEditButton?: boolean;
  hideSearch?: boolean;
  hideNewTemplateCTA?: boolean;
  externalSearchValue?: string;
  onEdit?: (templateId: string) => void;
  customTemplateClickHandler?: () => void;
  tabItemMap?: Record<string, UseTemplateFlowResult['tabItem']>;
};

type PolymorphicProps<C extends ElementType> = PolymorphicComponentPropWithoutRef<C, MultiTabTemplateSelectorProps>;

export const MultiTabTemplateSelector = <C extends ElementType = 'div'>({
  as,
  groupIds,
  templateTypes,
  staticDefaultTemplateTypes,
  onSelectTemplate,
  hideEditButton,
  onOpenSettings,
  settingsOpenDelay,
  hideSearch,
  hideNewTemplateCTA,
  externalSearchValue,
  onEdit,
  customTemplateClickHandler,
  tabItemMap,
  ...rest
}: PolymorphicProps<C>) => {
  const user = getUser();
  const { selectedOrgId } = useAppScopeStore();
  const { t } = useTranslation('integrated-messaging');
  const Component = as || 'div';
  const internalSearchField = useFormField({
    type: 'text',
  });
  const searchValue = hideSearch ? externalSearchValue : internalSearchField.value;
  const debouncedSearchValue = useDebouncedValue(searchValue, 150);
  const {
    lastTemplateUseTimestamps,
    updateTemplateLastUseTimestamp,
    userTemplateSortingPreferences: templateSortingPreference,
    setTemplateSortingPreference,
  } = useUpdateTemplateLastTimestamp();
  const userTemplateSortingPreference =
    (user?.userID ? templateSortingPreference[user?.userID] : undefined) ?? 'most-recent';
  const { onChange: _sortFieldOnChange, ...otherSortFieldProps } = useFormField(
    {
      type: 'dropdown',
      value: userTemplateSortingPreference,
    },
    [userTemplateSortingPreference]
  );
  const sortFieldOnChange = useCallback<typeof _sortFieldOnChange>(
    (e) => {
      if (!e || !('value' in e)) return;
      const typedEvent = e as { value: typeof userTemplateSortingPreference; name: string };
      if (user?.userID) setTemplateSortingPreference({ userId: user.userID, newPreference: typedEvent.value });
      _sortFieldOnChange(e);
    },
    [setTemplateSortingPreference, _sortFieldOnChange, user?.userID]
  );
  const templateTypeMap = useTemplateTypeMap();
  const sortTemplatesCallback = useCallback(
    (templates: Template[]) => {
      switch (userTemplateSortingPreference) {
        case 'most-recent':
          return sortTemplatesByLastUsage({
            templates,
            usageTimestamps: lastTemplateUseTimestamps,
          });
        case 'a-z':
          return [...templates].sort((a, b) => a.name.localeCompare(b.name));
        case 'z-a':
          return [...templates].sort((a, b) => b.name.localeCompare(a.name));
      }
    },
    [lastTemplateUseTimestamps, userTemplateSortingPreference]
  );
  const templatesListQuery = TemplatorV2Queries.useTemplatesList(
    {
      businessGroupIds: groupIds,
      ...(templateTypes?.length > 0 && { templateTypeSlugs: templateTypes }),
    },
    {
      select: (data) => {
        const sortedTemplates = sortTemplatesCallback(data.templates);
        const staticTemplates = (templateTypes ?? []).reduce<Template[]>((acc, type) => {
          if (staticDefaultTemplateTypes && !staticDefaultTemplateTypes.includes(type)) return acc;
          const newTemplate = getStaticTemplate(type, {
            orgId: selectedOrgId,
            businessGroupIds: groupIds,
            name: t('Default {{type}} Template', { type: templateTypeMap[type] }),
          });
          if (newTemplate) acc.push(newTemplate);
          return acc;
        }, []);
        const sortedTemplatesWithStatics = sortedTemplates?.concat(staticTemplates);

        if (!debouncedSearchValue) return { ...data, templates: sortedTemplatesWithStatics };
        return {
          ...data,
          templates: sortedTemplatesWithStatics?.filter(({ name, templateString }) =>
            templateMatchesSearch({ name, templateString }, debouncedSearchValue)
          ),
        };
      },
    }
  );
  const { navigate } = useSettingsNavigate();

  const handleSelectTemplate = (template: Template) => {
    onSelectTemplate?.(template);
    updateTemplateLastUseTimestamp({ templateId: template.templateId, templateType: template.templateTypeSlug });
    customTemplateClickHandler?.();
  };

  const handleSettingsNavigate: typeof navigate = (args) => {
    onOpenSettings?.();
    setTimeout(() => {
      navigate({ ...args, search: { ...args.search, closeOnSave: true, closeOnCancel: true } });
    }, settingsOpenDelay ?? SETTINGS_MODAL_INTERACTION_DELAY);
  };

  return (
    <Component
      css={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
      {...rest}
    >
      {!hideSearch && (
        <div
          css={{
            paddingBottom: theme.spacing(2),
            borderBottom: `1px solid ${theme.colors.neutral10}`,
            display: 'flex',
            width: '100%',
            gap: theme.spacing(1),
          }}
        >
          <DropdownField
            onChange={sortFieldOnChange}
            {...otherSortFieldProps}
            name={t('Sort by')}
            label=''
            startAdornment={<Icon name='sort-ascending' size={20} css={{ marginRight: theme.spacing(1) }} />}
            css={{ p: { paddingRight: theme.spacing(1) }, minWidth: 178 }} // To keep the dropdown width consistent when the value changes
          >
            <DropdownField.Option value='most-recent'>{t('Most Recent')}</DropdownField.Option>
            <DropdownField.Option value='a-z'>{t('A - Z')}</DropdownField.Option>
            <DropdownField.Option value='z-a'>{t('Z - A')}</DropdownField.Option>
          </DropdownField>
          <SearchField
            containerCss={css({ flexGrow: 1 })}
            name={t('Template search')}
            {...internalSearchField}
            autoFocus
          />
        </div>
      )}
      <ul
        css={{
          padding: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
        }}
      >
        {!hideNewTemplateCTA && (
          <NakedButton
            css={{
              padding: theme.spacing(2.5, 2),
              borderBottom: `1px solid ${theme.colors.neutral10}`,
              ':hover': {
                backgroundColor: theme.colors.neutral5,
              },
              ':focus ': {
                outline: 'none',
                boxShadow: `2px 0 0 ${theme.colors.primary50} inset`,
                backgroundColor: theme.colors.neutral5,
              },
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing(1),
              cursor: 'pointer',
            }}
            onClick={() => {
              handleSettingsNavigate({
                to: '/messaging/templates/create',
                search: !!templateTypes.length
                  ? {
                      type: templateTypes[0],
                    }
                  : undefined,
              });
            }}
            tabIndex={0}
            trackingId={`${TrackingPrefixes.TemplateSelector}-create-new-template-button`}
          >
            <Icon name='plus-small' size={20} css={{ color: theme.colors.primary50 }} />
            <Text color='primary' weight='bold' size='medium'>
              {t('Create New Template')}
            </Text>
          </NakedButton>
        )}
        {templatesListQuery.data?.templates?.map((template) => {
          const templateName = TemplateTypeTitleMap[template.templateTypeSlug] || '';
          const isDisabled = tabItemMap?.[templateName]?.disabled;
          const disabledLabel = tabItemMap?.[templateName]?.disabledLabel;

          return (
            <TemplateListItem
              key={template.templateId}
              groupIds={template.businessGroupIds}
              title={template.name}
              templateString={template.templateString}
              templateTypeSlug={template.templateTypeSlug}
              hideEditButton={hideEditButton || template.templateId.includes(STATIC_TEMPLATE_ID)}
              onEditButtonClick={(e: React.MouseEvent) => {
                if (isDisabled) return;
                if (onEdit) {
                  onEdit?.(template.templateId);
                  e.stopPropagation();
                  return;
                }
                handleSettingsNavigate({
                  to: '/messaging/templates/edit/:templateId',
                  params: { templateId: template.templateId },
                });
              }}
              onClick={() => {
                if (isDisabled) return;
                handleSelectTemplate(template);
              }}
              onKeyDown={(e: KeyboardEvent<HTMLLIElement>) => {
                if (isDisabled) return;
                if (e.key === 'Enter') handleSelectTemplate(template);
              }}
              trackingId={getManualTemplateTrackingId(
                template.templateTypeSlug,
                template.templateId.includes(STATIC_TEMPLATE_ID)
              )}
              showChevron={TemplateTypesWithProgressiveFlows.includes(template.templateTypeSlug)}
              isDisabled={isDisabled}
              disabledLabel={disabledLabel}
            />
          );
        })}
      </ul>
    </Component>
  );
};
